import React from 'react';
import { Avatar } from '../../components/avatar';
import { getInitials } from '../../utils/utils';

interface FacilityContactCardProps {
  facilityContact: {
    id: string;
    contact: {
      id: string;
      first_name?: string;
      last_name?: string;
      email?: string;
      phone_number?: string;
      phone_number_extension?: string;
    };
    role?: string;
  };
  onEdit?: (facilityContact: FacilityContactCardProps['facilityContact']) => void;
  onDelete?: (facilityContact: FacilityContactCardProps['facilityContact']) => void;
  roleMap?: Record<string, string>;
  canEdit?: boolean;
}

export const FacilityContactCard: React.FC<FacilityContactCardProps> = ({
  facilityContact,
  onEdit,
  onDelete,
  roleMap = {},
  canEdit = false,
}) => {
  const fullName =
    [facilityContact.contact.first_name, facilityContact.contact.last_name]
      .filter(Boolean)
      .join(' ') || 'Unnamed Contact';
  const initials = getInitials(fullName);

  return (
    <div className="tw:flex tw:flex-row tw:items-center tw:bg-zinc-100 tw:px-3 tw:py-2 tw:rounded-lg tw:gap-2 tw:dark:border tw:dark:border-white/10 tw:dark:bg-white/5 tw:dark:shadow-none">
      {/* Avatar */}
      <Avatar
        initials={initials}
        className="tw:shrink-0 tw:size-8 tw:text-white tw:bg-brand-green-0"
      />
      {/* Info grid: 2 rows x 2 columns */}
      <div className="tw:flex-grow tw:grid tw:grid-cols-[3fr_2fr] tw:gap-x-3 tw:gap-y-0.5 tw:min-w-0">
        {/* Row 1: Name | Role */}
        <div className="tw:text-xs tw:md:text-sm tw:text-zinc-700 tw:font-medium">{fullName}</div>
        <div className="tw:text-[0.625rem] tw:md:text-xs tw:text-zinc-400 tw:dark:text-zinc-300">
          {facilityContact.role ? (
            <span>{roleMap[facilityContact.role] || facilityContact.role}</span>
          ) : (
            <span className="tw:opacity-50">--</span>
          )}
        </div>
        {/* Row 2: Email | Phone */}
        <div className="tw:text-[0.625rem] tw:md:text-xs tw:text-zinc-700 tw:dark:text-zinc-200">
          {facilityContact.contact.email ? (
            <a
              href={`mailto:${facilityContact.contact.email}`}
              className="tw:flex tw:items-center tw:!no-underline"
              title={`Email ${facilityContact.contact.email}`}
            >
              <i className="fa-solid fa-envelope tw:mr-1.5 tw:text-zinc-400 tw:dark:text-zinc-300 tw:shrink-0" />
              <span>{facilityContact.contact.email}</span>
            </a>
          ) : (
            <span className="tw:opacity-50">--</span>
          )}
        </div>
        <div className="tw:text-[0.625rem] tw:md:text-xs tw:text-zinc-700 tw:dark:text-zinc-200">
          {facilityContact.contact.phone_number ? (
            <a
              href={`tel:${facilityContact.contact.phone_number}`}
              className="tw:flex tw:items-center tw:!no-underline"
              title={`Call ${facilityContact.contact.phone_number}`}
            >
              <i className="fa-solid fa-phone tw:mr-1.5 tw:text-zinc-400 tw:dark:text-zinc-300 tw:shrink-0" />
              <span>
                {facilityContact.contact.phone_number}
                {facilityContact.contact.phone_number_extension &&
                  ` ext. ${facilityContact.contact.phone_number_extension}`}
              </span>
            </a>
          ) : (
            <></>
          )}
        </div>
      </div>
      {/* Action buttons */}
      {canEdit && (
        <div className="tw:shrink-0 tw:flex tw:flex-col tw:gap-2 tw:items-center tw:self-center">
          {onEdit && (
            <button
              type="button"
              onClick={() => onEdit(facilityContact)}
              className="tw:text-zinc-400 tw:hover:text-brand-green-0 tw:border-none"
              aria-label="Edit Contact"
            >
              <i className="fa-regular fa-pen-to-square" />
            </button>
          )}
          {onDelete && (
            <button
              type="button"
              onClick={() => onDelete(facilityContact)}
              className="tw:text-zinc-400 tw:hover:text-stoplight-danger tw:border-none"
              aria-label="Delete Contact"
            >
              <i className="fa-regular fa-trash" />
            </button>
          )}
        </div>
      )}
    </div>
  );
};
